'use client';

import {
  TRIAL_UPGRADE_REQUEST_CALLBACK_ACTION,
  TRIALS_AND_UPGRADES_SCREEN,
  type TrialUpgradeRequestCallbackActionToken,
} from '@heidi/commerce/contracts';
import { useUserTier } from '@heidi/commerce/data';
import { useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl';

import { getCommerceTrialsUiPorts } from '../trials-ui-ports';
import { trialUpgradeRequestCallbackModalMessages } from './messages';
import type { TrialUpgradeRequestCallbackModalFormValues } from './schema';

type UseTrialUpgradeRequestCallbackModalParams = {
  open: boolean;
  close: () => void;
  closeModal: () => void;
  onSubmitForm: (
    data: TrialUpgradeRequestCallbackModalFormValues
  ) => Promise<{ ok: boolean }>;
};

/**
 * Analytics and action handlers for {@link TrialUpgradeRequestCallbackModal}.
 */
export function useTrialUpgradeRequestCallbackModal({
  open,
  close,
  closeModal,
  onSubmitForm,
}: UseTrialUpgradeRequestCallbackModalParams) {
  const intl = useIntl();
  const { userTier } = useUserTier();

  useEffect(() => {
    if (!open) return;
    getCommerceTrialsUiPorts().analytics.trackTrialUpgradeRequestCallbackViewed({
      screen_name: TRIALS_AND_UPGRADES_SCREEN.trialUpgradeRequestCallback,
    });
  }, [open]);

  const trackButton = useCallback(
    (action: TrialUpgradeRequestCallbackActionToken, label: string) => {
      getCommerceTrialsUiPorts().analytics.trackTrialUpgradeRequestCallbackClick({
        screen_name: TRIALS_AND_UPGRADES_SCREEN.trialUpgradeRequestCallback,
        action,
        label,
        ...(userTier !== undefined && { current_plan: userTier }),
      });
    },
    [userTier]
  );

  const handleBack = useCallback(() => {
    trackButton(
      TRIAL_UPGRADE_REQUEST_CALLBACK_ACTION.back,
      intl.formatMessage(trialUpgradeRequestCallbackModalMessages.back)
    );
    close();
  }, [close, intl, trackButton]);

  const handleCancel = useCallback(() => {
    trackButton(
      TRIAL_UPGRADE_REQUEST_CALLBACK_ACTION.cancel,
      intl.formatMessage(trialUpgradeRequestCallbackModalMessages.cancel)
    );
    close();
  }, [close, intl, trackButton]);

  const handleSubmit = useCallback(
    async (values: TrialUpgradeRequestCallbackModalFormValues) => {
      trackButton(
        TRIAL_UPGRADE_REQUEST_CALLBACK_ACTION.send,
        intl.formatMessage(trialUpgradeRequestCallbackModalMessages.send)
      );
      const result = await onSubmitForm({
        ...values,
        message: values.message?.trim() ?? '',
      });
      if (result.ok) {
        closeModal();
      }
    },
    [closeModal, intl, onSubmitForm, trackButton]
  );

  return { handleBack, handleCancel, handleSubmit };
}
